"use client";

import {Box, HStack, Icon, Stack, StackProps, Text} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import Heading from "./Heading";
import {StaticImageData} from "next/image";
import NextImage from "./Image";

export type FeatureT = {
  name?: string;
  summary: string;
  description: React.ReactNode;
  image: StaticImageData;
  icon: React.ComponentType;
};

const Feature = ({
  feature,
  isActive,
  colorPalette,
  ...props
}: {isActive?: boolean; feature: FeatureT; colorPalette: string} & StackProps) => (
  <Stack gap={3} role="group" color="gray.500" cursor="pointer" textAlign="left" {...props}>
    <Box w="full">
      <Icon
        color="white"
        p={2}
        h={9}
        w={9}
        bg={isActive ? `{colors.${colorPalette}.500}` : "gray.500"}
        bgGradient={
          isActive ? `linear-gradient(170deg, {colors.${colorPalette}.400}, {colors.${colorPalette}.500})` : undefined
        }
        rounded="lg"
      >
        <feature.icon />
      </Icon>
    </Box>
    {feature.name && (
      <Heading
        w="full"
        size="sm"
        fontWeight="500"
        color={isActive ? `{colors.${colorPalette}.500}` : "inherit"}
        _groupHover={{color: isActive ? `{colors.${colorPalette}.500}` : "gray.600"}}
        lineHeight="normal"
      >
        {feature.name}
      </Heading>
    )}
    <Text
      fontSize="xl"
      fontWeight="500"
      _groupHover={{color: isActive ? "gray.800" : "gray.700"}}
      color={isActive ? "gray.900" : "gray.500"}
      letterSpacing="tight"
      lineHeight="normal"
    >
      {feature.summary}
    </Text>
    <Text fontSize="sm" color={isActive ? "gray.600" : "gray.500"} _groupHover={{color: "gray.600"}}>
      {feature.description}
    </Text>
  </Stack>
);

const FeaturesMobile = ({features, colorPalette}: {features: FeatureT[]; colorPalette: string}) => (
  <Stack mt={20} gap={10} px={0}>
    {features.map((feature, i) => (
      <Box key={i}>
        <Feature feature={feature} mx="auto" maxW="2xl" isActive colorPalette={colorPalette} />
        <Box position="relative" mt="10" pb="10" mx={-6}>
          <Box
            position="absolute"
            bottom={0}
            top={8}
            left={0}
            right={0}
            bg="red.100"
            bgGradient="linear-gradient(180deg, {colors.gray.100}, white)"
          />
          <Box w="full" overflow="hidden" pl={8} py={1}>
            <Box
              position="relative"
              mx="auto"
              w="52.75rem"
              overflow="hidden"
              rounded="lg"
              bg="white"
              shadow="xl"
              ring={1}
              ringColor="gray.200"
            >
              <NextImage w="full" src={feature.image} alt="" />
            </Box>
          </Box>
        </Box>
      </Box>
    ))}
  </Stack>
);

const FeaturesDesktop = ({features, colorPalette}: {features: FeatureT[]; colorPalette: string}) => {
  const [tabIndex, _setTabIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);

  const setTabIndex = (v: number) => {
    setHasInteracted(true);
    _setTabIndex(v);
  };

  useEffect(() => {
    if (!hasInteracted) {
      const t = setInterval(() => _setTabIndex((tabIndex + 1) % features.length), 10000);
      return () => clearInterval(t);
    }
  }, [hasInteracted, tabIndex, features.length]);

  return (
    <>
      <HStack gap={8} align="flex-start">
        {features.map((feature, i) => (
          <Feature
            key={i}
            flex={1}
            isActive={i === tabIndex}
            feature={feature}
            colorPalette={colorPalette}
            onClick={() => setTabIndex(i)}
          />
        ))}
      </HStack>
      <Box mt="16" py={8} overflow="hidden" rounded="2xl" bg="gray.200" border="1px solid" borderColor="gray.300">
        <Box position="relative" transition=".5s ease" w="full">
          <Box px={8} w="70%" h="auto" visibility="hidden">
            <NextImage src={features[0].image} />
          </Box>
          {features.map((feature, i) => (
            <Box
              key={i}
              px={8}
              w="70%"
              position="absolute"
              top={0}
              transition=".5s ease"
              left={`${(i - tabIndex) * 70}%`}
              opacity={i === tabIndex ? 1 : 0.5}
              onClick={() => setTabIndex(i)}
              cursor="pointer"
            >
              <NextImage src={feature.image} shadow="md" rounded="xl" />
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export const FeatureCarousel = ({features, colorPalette}: {features: FeatureT[]; colorPalette: string}) => (
  <>
    <Box hideFrom="lg">
      <FeaturesMobile features={features} colorPalette={colorPalette} />
    </Box>
    <Box hideBelow="lg">
      <FeaturesDesktop features={features} colorPalette={colorPalette} />
    </Box>
  </>
);
